/**
 * Pagination Component
 * Reusable pagination UI that works with usePagination hook
 *
 * Usage:
 * const { paginatedItems, pagination } = usePagination(items);
 * <Pagination {...pagination} />
 */

import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    perPage: number;
    startIndex?: number;
    endIndex?: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
    goToPage: (page: number) => void;
    nextPage: () => void;
    prevPage: () => void;
    showItemCount?: boolean;
    showPageNumbers?: boolean;
    className?: string;
}

export function Pagination({
    currentPage,
    totalPages,
    totalItems,
    perPage,
    startIndex: startIndexProp,
    endIndex: endIndexProp,
    hasNextPage,
    hasPrevPage,
    goToPage,
    nextPage,
    prevPage,
    showItemCount = true,
    showPageNumbers = true,
    className = '',
}: PaginationProps) {
    if (totalPages <= 1) return null;

    // Calculate startIndex and endIndex if not provided
    const startIndex = startIndexProp ?? ((currentPage - 1) * perPage + 1);
    const endIndex = endIndexProp ?? Math.min(currentPage * perPage, totalItems);

    // Generate page numbers to show
    const getPageNumbers = () => {
        const pages: (number | 'ellipsis')[] = [];
        const maxVisiblePages = 5;

        if (totalPages <= maxVisiblePages) {
            // Show all pages if total is small
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            // Always show first page
            pages.push(1);

            if (currentPage > 3) {
                pages.push('ellipsis');
            }

            // Show pages around current
            const start = Math.max(2, currentPage - 1);
            const end = Math.min(totalPages - 1, currentPage + 1);

            for (let i = start; i <= end; i++) {
                pages.push(i);
            }

            if (currentPage < totalPages - 2) {
                pages.push('ellipsis');
            }

            // Always show last page
            pages.push(totalPages);
        }

        return pages;
    };

    return (
        <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 border-t border-gray-200 ${className}`}>
            {/* Item count */}
            {showItemCount && (
                <p className="text-sm text-gray-500">
                    Showing <span className="font-medium">{startIndex}</span> to{' '}
                    <span className="font-medium">{endIndex}</span> of{' '}
                    <span className="font-medium">{totalItems}</span> results
                </p>
            )}

            {/* Navigation */}
            <div className="flex items-center gap-1">
                {/* First page */}
                <button
                    onClick={() => goToPage(1)}
                    disabled={!hasPrevPage}
                    className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    title="First page"
                >
                    <ChevronsLeft size={16} />
                </button>

                {/* Previous */}
                <button
                    onClick={prevPage}
                    disabled={!hasPrevPage}
                    className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    title="Previous page"
                >
                    <ChevronLeft size={16} />
                </button>

                {/* Page numbers */}
                {showPageNumbers && (
                    <div className="hidden sm:flex items-center gap-1 mx-2">
                        {getPageNumbers().map((page, idx) => (
                            page === 'ellipsis' ? (
                                <span key={`ellipsis-${idx}`} className="px-2 text-gray-400">
                                    ...
                                </span>
                            ) : (
                                <button
                                    key={page}
                                    onClick={() => goToPage(page)}
                                    className={`min-w-[36px] h-9 px-3 rounded-lg text-sm font-medium transition-colors ${
                                        currentPage === page
                                            ? 'bg-primary text-white'
                                            : 'border border-gray-300 hover:bg-gray-50'
                                    }`}
                                >
                                    {page}
                                </button>
                            )
                        ))}
                    </div>
                )}

                {/* Mobile page indicator */}
                <span className="sm:hidden px-3 text-sm text-gray-600">
                    {currentPage} / {totalPages}
                </span>

                {/* Next */}
                <button
                    onClick={nextPage}
                    disabled={!hasNextPage}
                    className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    title="Next page"
                >
                    <ChevronRight size={16} />
                </button>

                {/* Last page */}
                <button
                    onClick={() => goToPage(totalPages)}
                    disabled={!hasNextPage}
                    className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    title="Last page"
                >
                    <ChevronsRight size={16} />
                </button>
            </div>
        </div>
    );
}

/**
 * Simple pagination - just prev/next with page count
 */
export function SimplePagination({
    currentPage,
    totalPages,
    nextPage,
    prevPage,
    hasPrevPage,
    hasNextPage,
}: Pick<PaginationProps, 'currentPage' | 'totalPages' | 'nextPage' | 'prevPage' | 'hasPrevPage' | 'hasNextPage'>) {
    if (totalPages <= 1) return null;

    return (
        <div className="flex items-center gap-2">
            <button
                onClick={prevPage}
                disabled={!hasPrevPage}
                className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
            >
                <ChevronLeft size={18} />
            </button>
            <span className="px-4 py-2 text-sm">
                Page {currentPage} of {totalPages}
            </span>
            <button
                onClick={nextPage}
                disabled={!hasNextPage}
                className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
            >
                <ChevronRight size={18} />
            </button>
        </div>
    );
}

export default Pagination;
